import React from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import {Stack, StackProps, useMultiStyleConfig} from '@chakra-ui/react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {HeaderLink, HideBelowViewport} from '_uiComponents'
import {HeaderActionType, useHeaderDispatch, useHeaderState, useGlobalConfigState} from '_context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useAnalytics, useViewport, useMinicart, useCurrentBasket, useWishlist, useOpenDrawer} from '_customHooks'
import {getAbsoluteUrl, isUserLogged} from '_utils'

interface TopbarRightProps {
    isInSidebar?: boolean
}

/**
 * @name TopbarRight
 *
 * @component
 *
 * @description
 * Renders a list of header links depending on the viewport.
 *
 * @param {TopbarRightProps} props - Properties object.
 * @param {boolean} props.isInSidebar - Indicates if the topbar right is rendered inside the sidebar.
 *
 * @returns {React.FC<TopbarRightProps>} - Rendered `TopbarRight` component.
 */
const TopbarRight: React.FC<TopbarRightProps> = ({isInSidebar}) => {
    const styles = useMultiStyleConfig('Topbar') as Record<string, StackProps>
    const {formatMessage} = useIntl()
    const {isDesktop} = useViewport()
    const {analyticsEventSearchLayer} = useAnalytics()
    const {isTransparentMode, isSearchSectionEnabled} = useHeaderState()
    const {dispatch} = useHeaderDispatch()
    const {openLoginDrawer} = useOpenDrawer()
    const {data: customer} = useCurrentCustomer()
    const {wishlistProductsCount} = useWishlist()
    const {appPreferences} = useGlobalConfigState()
    const {hostLink, PhoneLogin, CountriesWithZeroPrices} = appPreferences
    const {openMinicart} = useMinicart()
    const {data: basket} = useCurrentBasket()
    const basketProductsCount = basket?.c_productsQuantity || 0
    const {locale, site} = useMultiSite()

    // Icon size must NOT depend on `isDesktop` (useViewport → user-agent): that value differs between
    // the cached SSR HTML and the client on another device, which changed the icon's emotion className
    // and caused a hydration mismatch. `header-action` is a CSS-responsive token (20px mobile → 16px
    // from lg), so the SSR markup is identical for every device and CDN-cache-safe. In the sidebar
    // (mobile-only, client-rendered drawer) keep the fixed small size as before.
    const iconSize = isInSidebar ? 'sm' : 'header-action'
    const colorMode = isTransparentMode ? 'light' : 'dark'

    const aliasCountry = locale?.alias?.split('-')?.[1]?.toLowerCase()
    const hostLinkLocalized = (hostLink ? JSON.parse(hostLink)[site.id] : undefined) as string | undefined
    const hasHiddenPrices = !!CountriesWithZeroPrices?.find((country: string) => country.toLowerCase() === aliasCountry)
    const wishlistHref = isUserLogged(customer) ? `/${locale.alias}/account?tabID=wishlist` : `/${locale.alias}/wishlist`

    /**
     * Handles account button click.
     *
     * @description
     * Redirects to account page if user is logged, otherwise to login page.
     */
    const openAccount = () => {
        if (hostLinkLocalized) {
            if (customer?.isGuest) {
                if (!isDesktop) {
                    window.location.href = getAbsoluteUrl(hostLinkLocalized, locale.alias, '/login?rurl=1')
                } else {
                    openLoginDrawer({
                        phonelogin: PhoneLogin,
                        hostlink: hostLink,
                    })
                }
            } else {
                window.location.href = getAbsoluteUrl(hostLinkLocalized, locale.alias, '/account')
            }
        }
    }

    const onSearchClick = () => {
        dispatch({type: HeaderActionType.TOGGLE_SEARCH_SECTION})
        analyticsEventSearchLayer({action: !isSearchSectionEnabled ? 'open' : 'close'})
    }

    const handleCartClick = () => {
        if (isDesktop) {
            openMinicart(hasHiddenPrices)
        } else if (basket?.c_goToCartUrl) {
            // Mobile: navigate to the SFRA cart. Done in the handler (not via `href`) so the rendered
            // markup does NOT depend on `isDesktop` — see the note on the cart HeaderLink below.
            window.location.href = basket.c_goToCartUrl
        }
    }

    return (
        <Stack {...(!isInSidebar ? styles.topbarRight : styles.topbarRightSidebar)} data-testid="topbar-right">
            {!isInSidebar ? (
                <>
                    <HeaderLink
                        iconName="IconSearch"
                        iconSize={iconSize}
                        colorMode={colorMode}
                        shouldShowText={false}
                        id="tobpar-search-trigger"
                        ariaLabel={formatMessage({id: 'alt.link.search', defaultMessage: 'Search'})}
                        onClick={onSearchClick}
                    />

                    <HideBelowViewport breakpoint="lg">
                        <HeaderLink
                            href={wishlistHref}
                            isExternal={isUserLogged(customer)}
                            iconName="IconHeart"
                            iconSize={iconSize}
                            itemsCount={wishlistProductsCount}
                            colorMode={colorMode}
                            shouldShowText={false}
                            scrollToTop
                            ariaLabel={formatMessage({id: 'alt.link.wishlist', defaultMessage: 'Wishlist'})}
                        />
                    </HideBelowViewport>

                    <HideBelowViewport breakpoint="lg">
                        <HeaderLink
                            iconName="IconUser"
                            iconSize={iconSize}
                            colorMode={colorMode}
                            shouldShowText={false}
                            ariaLabel={formatMessage({id: 'alt.link.profile', defaultMessage: 'Log in or go to the Personal Area'})}
                            onClick={openAccount}
                        />
                    </HideBelowViewport>

                    {/* NO `href={!isDesktop ? ... : undefined}` here. `href` presence flips HeaderLink's
                        root element (Flex/<div> when absent → Link/<a> when present, see HeaderLink
                        `as={href ? ... : Flex}`). Since `isDesktop` comes from useViewport (user-agent),
                        the SSR HTML (cached on the CDN) and the client on another device would disagree on
                        the element type → hard hydration mismatch. The cart destination is device-specific
                        behavior, handled in `handleCartClick` (minicart on desktop, cart URL on mobile),
                        keeping the markup identical for every device. */}
                    <HeaderLink
                        iconName="IconCart"
                        iconSize={iconSize}
                        colorMode={colorMode}
                        shouldShowText={false}
                        itemsCount={basketProductsCount}
                        ariaLabel={formatMessage(
                            {
                                id: 'label.cart.itemNum',
                                defaultMessage: '{0} items in the cart',
                            },
                            {0: basketProductsCount},
                        )}
                        onClick={handleCartClick}
                    />
                </>
            ) : (
                <>
                    <HeaderLink iconName="IconUser" iconSize={iconSize} colorMode={colorMode} isInSidebar={true} onClick={openAccount}>
                        <FormattedMessage id={'label.link.account'} defaultMessage="Personal area" />
                    </HeaderLink>

                    <HeaderLink
                        href={wishlistHref}
                        isExternal={isUserLogged(customer)}
                        iconName="IconHeart"
                        iconSize={iconSize}
                        itemsCount={wishlistProductsCount}
                        colorMode={colorMode}
                        shouldShowText={false}
                        isInSidebar={isInSidebar}
                        scrollToTop
                    >
                        <FormattedMessage id={'label.link.wishlist'} defaultMessage="My Wishlist" />
                    </HeaderLink>
                </>
            )}
        </Stack>
    )
}

export default TopbarRight
